/* eslint-disable react/display-name */
import React, { useCallback, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';
import styles from 'src/styles/layout.module.css';
import { Button, DatePicker, Form, Input, InputNumber, Row, Select, Space, Typography } from 'antd';
import { CompanyDetails } from 'src/features/companies/companies.types';
import {
  addDetailsToCompany,
  deleteDetailsFromCompany,
  saveCompanyDetails,
} from 'src/features/companies/companies.state';
import moment from 'moment';
import { DeleteOutlined, EditTwoTone, MinusSquareOutlined, PlusSquareOutlined, ShopTwoTone } from '@ant-design/icons';
import tileStyles from 'src/styles/tile.module.css';

import { useCompanyDetailsById } from 'src/features/companies/companies.hooks';
import ConfirmButton from 'src/components/atoms/ConfirmButton';

const { Option } = Select;

interface CompanyDetailsFormProps {
  details?: CompanyDetails;
  onSave?: (details: CompanyDetails) => void;
}

const CompanyDetailsForm: React.FC<CompanyDetailsFormProps> = props => {
  const { details, onSave } = props;
  const [newDetails, setNewDetails] = useState<CompanyDetails>(details || ({} as CompanyDetails));
  console.log(newDetails);
  return (
    <>
      <Form
        labelCol={{
          span: 4,
        }}
        wrapperCol={{
          span: 14,
        }}
        layout='horizontal'
      >
        <Form.Item label='Наименование юр.лица'>
          <Input
            value={newDetails.legalEntityName}
            onChange={e =>
              setNewDetails(nd => ({
                ...nd,
                legalEntityName: e.target.value,
              }))
            }
          />
        </Form.Item>
        <Form.Item label='Юридический адрес'>
          <Input
            value={newDetails.legalEntityAddress}
            onChange={e =>
              setNewDetails(nd => ({
                ...nd,
                legalEntityAddress: e.target.value,
              }))
            }
          />
        </Form.Item>
        <Form.Item label='Фактический адрес'>
          <Input
            value={newDetails.address}
            onChange={e =>
              setNewDetails(nd => ({
                ...nd,
                address: e.target.value,
              }))
            }
          />
        </Form.Item>
        <Form.Item label='Номер договора'>
          <Input
            value={newDetails.contractLabel}
            onChange={e =>
              setNewDetails(nd => ({
                ...nd,
                contractLabel: e.target.value,
              }))
            }
          />
        </Form.Item>
        <Form.Item label='Тип договора'>
          <Select
            showSearch
            value={newDetails.contractType}
            onChange={value =>
              setNewDetails(nd => ({
                ...nd,
                contractType: value,
              }))
            }
          >
            <Option value='lease_contract'>Договор аренды</Option>
            <Option value='agent_contract'>Агентский договор</Option>
            <Option value='mixed_contract'>Смешанный договор</Option>
            <Option value='service_agreement'>Договор оказания услуг</Option>
          </Select>
        </Form.Item>
        <Form.Item label='Дата подписания договора'>
          <DatePicker
            value={newDetails.contractSigningDate ? moment(newDetails.contractSigningDate, 'YYYY-MM-DD') : null}
            onChange={(date, dateString) =>
              setNewDetails(nd => ({
                ...nd,
                contractSigningDate: dateString,
              }))
            }
          />
        </Form.Item>
        <Form.Item label='Дата окончания договора'>
          <DatePicker
            value={newDetails.contractExpirationDate ? moment(newDetails.contractExpirationDate, 'YYYY-MM-DD') : null}
            onChange={(date, dateString) =>
              setNewDetails(nd => ({
                ...nd,
                contractExpirationDate: dateString,
              }))
            }
          />
        </Form.Item>
        <Form.Item label='Комиссия агента'>
          <InputNumber
            value={newDetails.agentCommission}
            onChange={value =>
              setNewDetails(nd => ({
                ...nd,
                agentCommission: value as number,
              }))
            }
          />
          &nbsp; %
        </Form.Item>
        <Form.Item label='Количество шлемов'>
          <InputNumber
            value={newDetails.headsets}
            onChange={value =>
              setNewDetails(nd => ({
                ...nd,
                headsets: value as number,
              }))
            }
          />
        </Form.Item>
        <Form.Item label='Контакты'>
          <Input.TextArea
            value={newDetails.contacts}
            onChange={e =>
              setNewDetails(nd => ({
                ...nd,
                contacts: e.target.value,
              }))
            }
          />
        </Form.Item>
        <Form.Item
          wrapperCol={{
            offset: 4,
            span: 16,
          }}
        >
          <Button type='primary' htmlType='submit' onClick={() => onSave && onSave(newDetails)}>
            Сохранить
          </Button>
        </Form.Item>
      </Form>
    </>
  );
};

const CompanyDetailsTab: React.FC = () => {
  const { companyId } = useParams() as { companyId: string };
  const details = useCompanyDetailsById(companyId) as CompanyDetails[];
  const dispatch = useDispatch();
  const saveDetails = useCallback(
    (details: CompanyDetails) => {
      const { id } = details;
      if (id) {
        dispatch(saveCompanyDetails({ companyId, recordId: id, details }));
      } else {
        dispatch(addDetailsToCompany({ companyId, details }));
      }
    },
    [dispatch, companyId]
  );
  const deleteDetails = useCallback(
    (id: string) => {
      dispatch(deleteDetailsFromCompany({ companyId, recordId: id }));
    },
    [dispatch, companyId]
  );

  const [createMode, setCreateMode] = useState<boolean>(false);
  const [editedRecord, setEditedRecord] = useState<CompanyDetails | undefined>(undefined);

  return (
    <div className={styles.root}>
      <div className={styles.header}>
        <Typography.Title level={4}>О компании</Typography.Title>
        <Button type='primary' onClick={() => setCreateMode(m => !m)}>
          {createMode ? <MinusSquareOutlined /> : <PlusSquareOutlined />}
        </Button>
      </div>
      {createMode && <CompanyDetailsForm details={editedRecord} onSave={saveDetails} />}
      <div className={styles.content}>
        {details.map(v => (
          <div key={v.id} className={tileStyles.listTile}>
            <Row justify='space-between' style={{ width: '100% ' }}>
              <Space>
                <ShopTwoTone />
                {v.legalEntityName}
              </Space>
              <Space direction={'horizontal'} style={{ justifyContent: 'end' }}>
                <Button
                  onClick={() => {
                    setEditedRecord(v);
                    setCreateMode(true);
                  }}
                >
                  <EditTwoTone />
                </Button>
                <ConfirmButton danger type='primary' onClick={() => deleteDetails(v.id)}>
                  <DeleteOutlined />
                </ConfirmButton>
              </Space>
            </Row>
          </div>
        ))}
      </div>
    </div>
  );
};

export default CompanyDetailsTab;
